import { HttpClient, HttpDownloadProgressEvent, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { ChatResponse } from '../models/chat-response.model';
import { ResponsePackage } from '../models/util.model';
import { SHARED_LIB_CONFIG, SharedLibConfig } from '../config/lib-config';

export interface ChatStartResponse {
  thread_id: string;
  threadId?: string;
}

export interface ExampleQuestionsResponse {
  questions: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private pathApi: string;

  constructor(private http: HttpClient, @Inject(SHARED_LIB_CONFIG) private config: SharedLibConfig) {
    this.pathApi = this.config.pathApi + 'chat/';
  }

  async startChat(): Promise<ChatStartResponse> {
    const responsePackage = await firstValueFrom(
      this.http.post<ResponsePackage<ChatStartResponse>>(`${this.pathApi}start`, {})
    );
    const threadId = responsePackage.data?.thread_id ?? responsePackage.data?.threadId ?? '';

    return {
      thread_id: threadId
    };
  }

  async getExampleQuestions(): Promise<string[]> {
    const responsePackage = await firstValueFrom(
      this.http.get<ResponsePackage<ExampleQuestionsResponse>>(`${this.pathApi}example-questions`)
    );

    return responsePackage?.data?.questions ?? [];
  }

  sendMessage(message: string, threadId: string): Observable<ChatResponse> {
    return new Observable<ChatResponse>((subscriber) => {
      let buffer = '';
      let processedLength = 0;

      const subscription = this.http.post(`${this.pathApi}send`, {
        message,
        thread_id: threadId
      }, {
        observe: 'events',
        reportProgress: true,
        responseType: 'text'
      }).subscribe({
        next: (event: HttpEvent<string>) => {
          if (event.type === HttpEventType.DownloadProgress) {
            const partialText = (event as HttpDownloadProgressEvent).partialText ?? '';
            const chunk = partialText.substring(processedLength);
            processedLength = partialText.length;
            buffer += chunk;
            buffer = this.parseResponseBuffer(buffer, (chatResponse) => subscriber.next(chatResponse));
            return;
          }

          if (event instanceof HttpResponse) {
            const responseText = event.body ?? '';

            if (responseText.length > processedLength) {
              buffer += responseText.substring(processedLength);
              processedLength = responseText.length;
            }

            this.parseResponseBuffer(buffer, (chatResponse) => subscriber.next(chatResponse), true);
          }
        },
        error: (error) => subscriber.error(error),
        complete: () => subscriber.complete()
      });

      return () => subscription.unsubscribe();
    });
  }

  private parseResponseBuffer(
    buffer: string,
    onResponse: (response: ChatResponse) => void,
    flush: boolean = false
  ): string {
    const parts = buffer.split(/\r?\n\r?\n|\r?\n/);
    const remaining = flush ? '' : parts.pop() ?? '';

    parts.forEach((part) => {
      const json = this.extractJsonPayload(part);

      if (!json) {
        return;
      }

      try {
        onResponse(JSON.parse(json) as ChatResponse);
      } catch {
        onResponse({
          type: 'error',
          data: 'Received an invalid chat response.'
        });
      }
    });

    return remaining;
  }

  private extractJsonPayload(part: string): string | null {
    const dataLines = part
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.substring(5).trim())
      .filter((line) => line && line !== '[DONE]');

    if (dataLines.length) {
      return dataLines.join('');
    }

    const trimmedPart = part.trim();
    return trimmedPart.startsWith('{') ? trimmedPart : null;
  }
}
